export type Disposer = () => void;

export interface DisposerBag {
  readonly disposed: boolean;
  add: (disposer: Disposer) => Disposer;
  addEventListener: (
    target: EventTarget | null | undefined,
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ) => Disposer;
  dispose: Disposer;
}

export const once = (disposer: Disposer): Disposer => {
  let disposed = false;

  return () => {
    if (disposed) return;
    disposed = true;
    disposer();
  };
};

export const createDisposerBag = (): DisposerBag => {
  let disposed = false;
  const disposers: Disposer[] = [];

  const disposeSafely = (disposer: Disposer) => {
    try {
      disposer();
    } catch {
      // Best-effort cleanup; one failing disposer should not block the rest.
    }
  };

  const bag: DisposerBag = {
    get disposed() {
      return disposed;
    },
    add(disposer) {
      const wrapped = once(disposer);
      if (disposed) {
        disposeSafely(wrapped);
      } else {
        disposers.push(wrapped);
      }
      return wrapped;
    },
    addEventListener(target, type, listener, options) {
      if (!target) return once(() => {});
      target.addEventListener(type, listener, options);
      return bag.add(() => target.removeEventListener(type, listener, options));
    },
    dispose: once(() => {
      disposed = true;
      const pending = disposers.splice(0).reverse();
      pending.forEach(disposeSafely);
    }),
  };

  return bag;
};

export const ownWindowValue = <T>(
  key: string,
  value: T,
  resetValue: unknown = null,
): Disposer => {
  if (typeof window === "undefined") return once(() => {});

  const win = window as unknown as Window & Record<string, unknown>;
  win[key] = value;

  return once(() => {
    if (win[key] === value) {
      win[key] = resetValue;
    }
  });
};
